import { useFetchLatestAppoinmentResults } from '@/api/hooks/useFetchLatestAppointmentResults'
import { useFetchUpcomingAppointment } from '@/api/hooks/useFetchUpcomingAppointment'
import type { UpcomingPatientAppointment } from '@/api/types/ProviderApi'
import { ResultConditionsList } from '@/components/AppointmentResultsPage/ResultsConditionsList'
import { WallyHealthScore } from '@/components/AppointmentResultsPage/WallyHealthScore'
import { ScheduleAppointmentButton } from '@/components/ScheduleAppointmentButton'
import { Header } from '@/ui/core/header'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { View, useWindowDimensions } from 'react-native'
import {
	LocationIdNameMap,
	NextStepsCopyMap,
	UpcomingAppointmentCopyMap,
} from './constants'

const HEADER_HEIGHT = 96

const BookAppointmentNextSteps = ({
	nextAppointmentType,
}: { nextAppointmentType: string }) => (
	<VStack className="lg:pr-0 lg:pl-0 max-w-[30rem] pr-8 gap-8">
		<Text>{NextStepsCopyMap[nextAppointmentType ?? 'DEFAULT']}</Text>
		<ScheduleAppointmentButton />
	</VStack>
)

const UpcomingAppointmentNextSteps = ({
	upcomingAppointment,
	nextAppointmentType,
}: { upcomingAppointment: UpcomingPatientAppointment; nextAppointmentType?: string }) => {
	const upcomingAppointmentText = `We'll see you at your upcoming appointment at our ${
		LocationIdNameMap[upcomingAppointment.locationId]
	} studio on `

	if (!nextAppointmentType) {
		return null
	}

	return (
		<VStack className="lg:pr-0 max-w-[30rem] lg:pl-0 pr-8 gap-8">
			<Text>
				{upcomingAppointmentText}
				<Text className="font-primary-bold underline">
					{upcomingAppointment.startTimeFormatted}
				</Text>
				.
			</Text>
			<Text>{UpcomingAppointmentCopyMap[nextAppointmentType!]}</Text>
		</VStack>
	)
}

const ViewableAppointmentResults = () => {
	const { appointmentResults } = useFetchLatestAppoinmentResults()
	const { height: screenHeight } = useWindowDimensions()
	const { hasUpcomingAppointment, appointment: upcomingAppointment } =
		useFetchUpcomingAppointment()

	return (
		<VStack gap={36} fullWidth>
			<VStack fullWidth style={{ height: screenHeight - HEADER_HEIGHT }}>
				<VStack fullWidth className="gap-16 flex-1 justify-start items-center">
					<VStack gap={24} className="items-center justify-center">
						<Header className="text-center lg:text-5xl text-3xl">
							Your personalized report.
						</Header>
						<Text className="max-w-[22rem] text-center">
							{appointmentResults?.summary}
						</Text>
					</VStack>
					<VStack className="items-center justify-center" gap={32}>
						<WallyHealthScore score={appointmentResults?.healthScore} />
						<Header className="tracking-[.15em]" variant="h3">
							YOUR WALLY SCORE
						</Header>
						<Text className="max-w-60 text-pretty text-center">
							Your Wally Score takes into account your current tooth and gum health, along
							with insights found from x-rays and scans taken in our studios.
						</Text>
					</VStack>
				</VStack>
			</VStack>
			<VStack className="items-center">
				<ResultConditionsList conditions={appointmentResults?.conditions} />
				{appointmentResults?.canBookNextAppointment && (
					<VStack className="items-center justify-center gap-8 pb-16 px-12">
						<View className="w-full flex items-start ios:self-start android:self-start">
							<Header className="text-3xl">Next steps.</Header>
						</View>
						{!hasUpcomingAppointment && (
							<BookAppointmentNextSteps
								nextAppointmentType={appointmentResults.nextAppointmentType}
							/>
						)}
						{hasUpcomingAppointment && (
							<UpcomingAppointmentNextSteps
								upcomingAppointment={upcomingAppointment!}
								nextAppointmentType={upcomingAppointment!.types[0]}
							/>
						)}
					</VStack>
				)}
			</VStack>
		</VStack>
	)
}

export { ViewableAppointmentResults }
